import { useState, useEffect } from "react";
import { Description, Field, Label, Switch } from "@headlessui/react";
import { SelectListItem } from "../../components/input/SelectList";
import SimpleListbox, { SimpleOption } from "../../components/input/SimpleListbox";
import { AdvancedSettingsViewModel } from "./promptsApiSlice";
import {
	TAP_GUIDE_ORGANIZER_ID,
	TAP_GUIDE_ORGANIZER_NAME,
} from "../writing/organizing/organizingApiSlice";

interface OrganizerStepProps {
	model: AdvancedSettingsViewModel;
	organizersByGenre: SelectListItem[];
	genres: SelectListItem[];
	genreId: number;
	setAdvancedModel: (model?: AdvancedSettingsViewModel) => void;
}

export default function OrganizerStep(props: OrganizerStepProps) {
	const { model, organizersByGenre, genres, genreId, setAdvancedModel } = props;

	const genreName = genres.find((g) => g.value == genreId.toString())?.text ?? null;

	const organizerOptions = organizersByGenre
		.filter((sli) => sli.text !== TAP_GUIDE_ORGANIZER_NAME)
		.filter((sli) => sli.group == null || sli.group.name == genreName)
		.map((sli) => {
			return { value: sli.value.toString(), label: sli.text } as SimpleOption;
		});

	const [organizerRequired, setOrganizerRequired] = useState(model.isOrganizerRequired);
	const [selectedOrganizerId, setSelectedOrganizerId] = useState(
		model.selectedOrganizerId !== TAP_GUIDE_ORGANIZER_ID ? model.selectedOrganizerId : undefined
	);

	useEffect(() => {
		setAdvancedModel(updateModel());
	}, [organizerRequired, selectedOrganizerId]);

	function updateModel(): AdvancedSettingsViewModel | undefined {
		if (!model) return undefined;
		const advancedModel: AdvancedSettingsViewModel = {
			...model,
			isOrganizerRequired: organizerRequired,
			selectedOrganizerId: selectedOrganizerId,
		};
		return advancedModel;
	}

	return (
		<section className="space-y-6">
			<div className="flex pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Organizer</h2>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Assign Organizer
						</Label>
						<Switch
							checked={organizerRequired}
							onChange={setOrganizerRequired}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
					{organizerRequired && (
						<Field>
							<Label className="block font-medium leading-6">Select Organizer</Label>
							<Description className="text-sm leading-6 text-gray-600">
								{genreId === 0
									? 'Select a genre on "Step 1: Prompt" to see more options for organizers.'
									: "Organizer options are genre specific. If you change the genre, you may see different options."}
							</Description>
							<SimpleListbox
								id="organizers"
								options={organizerOptions}
								value={selectedOrganizerId ?? organizerOptions[0].value}
								className={
									"w-full focus:ring-1 focus:ring-inset focus:!ring-primary-600 !py-1.5 mt-2"
								}
								onChange={(e) => setSelectedOrganizerId(Number(e.target.value))}
							/>
						</Field>
					)}
				</div>
			</div>
		</section>
	);
}
